import { InputIcon, InputSlot } from '@vidahealth/vida-ui';
import { Eye, EyeOff } from 'lucide-react-native';
import { type ComponentProps, useState } from 'react';
import { type FieldValues } from 'react-hook-form';
import { Platform } from 'react-native';

import { TextField } from '../TextField';

// On web, rapid clicks on a Pressable can trigger the browser's text-selection
// gesture and latch onto adjacent text (the input value or surrounding
// labels). Calling preventDefault on mousedown stops the browser from
// starting a selection in response to the click; the click event still fires
// so the toggle works normally. On native this prop is ignored.
const stopSelectionOnPress =
  Platform.OS === 'web'
    ? // react-native-web forwards onMouseDown to the underlying DOM element,
      // but RN's type defs don't model it — cast to keep the call site clean.
      ({
        onMouseDown: (event: { preventDefault: () => void }) => event.preventDefault(),
      } as Record<string, unknown>)
    : {};

type TextFieldProps<TFieldValues extends FieldValues> = ComponentProps<
  typeof TextField<TFieldValues>
>;

type SensitiveFieldProps<TFieldValues extends FieldValues> = Omit<
  TextFieldProps<TFieldValues>,
  'secureTextEntry' | 'endSlot'
>;

/**
 * TextField variant for inputs that should be masked by default with a
 * reveal toggle. Use for any field whose value shouldn't appear in
 * shoulder-surf views, iOS screenshots, or session-replay tooling — this
 * includes passwords *and* secondary identifiers like SSN (full or last-4).
 *
 * Datadog Session Replay and PostHog auto-redact inputs with
 * `secureTextEntry`, so wrapping sensitive numeric inputs in this component
 * keeps them out of analytics replays without per-tool config.
 *
 * Callers control `autoComplete` / `textContentType` themselves — defaults
 * would be misleading here since this serves both password and non-password
 * use cases. For passwords, pass `autoComplete='current-password' /
 * 'new-password'`; for SSN-style inputs, pass `autoComplete='off'`.
 */
export function SensitiveField<TFieldValues extends FieldValues>(
  props: SensitiveFieldProps<TFieldValues>
) {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <TextField
      {...(props as TextFieldProps<TFieldValues>)}
      secureTextEntry={!isVisible}
      autoCapitalize='none'
      autoCorrect={false}
      endSlot={
        <InputSlot onPress={() => setIsVisible((v) => !v)} {...stopSelectionOnPress}>
          <InputIcon as={isVisible ? EyeOff : Eye} className='text-typography-500' />
        </InputSlot>
      }
    />
  );
}
